// IntroductionStep.tsx
import React, { useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert
} from '@mui/material';
import { SignupData } from 'form-application-shared';

interface IntroductionStepProps {
  formData: SignupData;
  setFormData: React.Dispatch<React.SetStateAction<SignupData>>;
  onValidationChange: (validationFn: () => boolean) => void;
}

const IntroductionStep: React.FC<IntroductionStepProps> = ({ 
  formData, 
  setFormData, 
  onValidationChange 
}) => {
  
  // This step doesn't require validation - always valid
  useEffect(() => {
    onValidationChange(() => true);
  }, [onValidationChange]);

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
        FORMATION APPLICATION CHECKLIST AND INFORMATION
      </Typography>
      
      <Typography variant="subtitle1" align="center" sx={{ mb: 4, fontStyle: 'italic' }}>
        The following items, included in the formation document, should be completed in their entirety.
      </Typography>

      <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Please complete the following items:
        </Typography>

        <List sx={{ mt: 2 }}>
          <ListItem sx={{ alignItems: 'flex-start', py: 2 }}>
            <ListItemText
              primary={
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Name Choices
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ mt: 1 }}>
                  While more often than not, the first choice of names is available. However, we 
                  recommend that you choose a 2<sup>nd</sup> and 3<sup>rd</sup> name choice preference. 
                  Please be as creative as possible. Do not use initials or common industry terms such 
                  as auto, express, cars, credit, etc., as those are most likely taken.
                </Typography>
              }
            />
          </ListItem>

          <Divider />

          <ListItem sx={{ alignItems: 'flex-start', py: 2 }}>
            <ListItemText
              primary={
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Signature
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ mt: 1 }}>
                  DocuSign will be sent after application submission.Be sure you sign & date the formation document.
                </Typography>
              }
            />
          </ListItem>

          <Divider />

          <ListItem sx={{ alignItems: 'flex-start', py: 2 }}>
            <ListItemText
              primary={
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Reinsurance Company Contact Information
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This is the person you want us to communicate with regarding reinsurance company matters.
                </Typography>
              }
            />
          </ListItem>

          <Divider />

          <ListItem sx={{ alignItems: 'flex-start', py: 2 }}>
            <ListItemText
              primary={
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Producer Information
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This is the entity that will be producing the contracts that are to be reinsured. 
                  This is typically the dealership or finance company. Please use all legal names.
                </Typography>
              }
            />
          </ListItem>

          <Divider />

          <ListItem sx={{ alignItems: 'flex-start', py: 2 }}>
            <ListItemText
              primary={
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Shareholders
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This is important as the Delaware Tribe of Indians uses this information to approve 
                  all reinsurance company shareholders. Please use all legal names. Please list all Shareholders of the Reinsurance Company. 
                  Your Shareholder total must equal 100%.
                </Typography>
              }
            />
          </ListItem>

          <Divider />

          <ListItem sx={{ alignItems: 'flex-start', py: 2 }}>
            <ListItemText
              primary={
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Directors
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ mt: 1 }}>
                  The Delaware Tribe of Indians requires a minimum of two Directors for each reinsurance company.
                </Typography>
              }
            />
          </ListItem>

          <Divider />

          <ListItem sx={{ alignItems: 'flex-start', py: 2 }}>
            <ListItemText
              primary={
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Officers
                </Typography>
              }
              secondary={
                <Typography variant="body2" sx={{ mt: 1 }}>
                  These may be the same as the Directors, however, the same information will need to be 
                  supplied to the Delaware Tribe of Indians for the vetting process. An individual may take 
                  on more than one role, however the President and Secretary are mandatory, and must be two 
                  different people.
                </Typography>
              }
            />
          </ListItem>
        </List>
      </Paper>

      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Identification Documents:
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" paragraph>
            Government issued picture IDs (Passports or Driver's Licenses) must be provided to the 
            Delaware Tribe of Indians, with the application, for all Shareholders, Directors and Officers.
          </Typography>

          <Typography variant="body2" paragraph>
            The IDs can be color copies or photos at a resolution of 300dpi is highly recommended. 
            Each shareholder, director and officer must provide this with the application.
          </Typography>

          <Typography variant="body2" paragraph>
            If the shareholder is a Trust or LLC, the Trustee or Managing Member (in the case of an LLC) 
            must submit the same information as an individual shareholder, as well as a copy of the trust, 
            or registration for the LLC in your state.
          </Typography>
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mt: 4 }}>
        <Typography variant="body2">
          Please review this information carefully before proceeding. The following steps will guide you 
          through providing all the required information and documents.
        </Typography>
      </Alert>
    </Box>
  );
};

export default IntroductionStep;
